import React from "react";
import { useNavigate } from "react-router-dom";

const labelMap = {
  displayName: "Display name",
  heroImageDesktop: "Storefront banner image",
  primaryCategories: "Primary categories",
  policyTerms: "Store terms",
  policyRefund: "Refund policy",
  policyCancellation: "Cancellation policy",
};

const setupStatusLabelMap = {
  draft: "Setup required",
  ready: "Ready",
  published: "Published",
};

function StorefrontSetupStatusBanner({
  statusData,
  isMobile = false,
  setupPath,
  onSetupClick,
}) {
  const navigate = useNavigate();

  if (!statusData) return null;

  const {
    setupStatus,
    completionPercent,
    missingRequirements = [],
    missingRequirementLabels = [],
    notifications = [],
    canPublish = true,
    canRemainVisible = true,
    requiredFields = {},
  } = statusData;

  const isComplete = setupStatus === "ready" || setupStatus === "published";
  const canOpenSetup = Boolean(onSetupClick || setupPath);

  const handleSetupClick = () => {
    if (onSetupClick) {
      onSetupClick();
      return;
    }
    if (setupPath) {
      navigate(setupPath);
    }
  };

  return (
    <div
      style={{
        background: "#ffffff",
        border: "1px solid #dbeafe",
        borderLeft: "4px solid #2563eb",
        borderRadius: "10px",
        padding: isMobile ? "12px" : "14px 16px",
        marginBottom: "16px",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: "12px",
          flexWrap: "wrap",
        }}
      >
        <div>
          <div
            style={{
              fontSize: isMobile ? "14px" : "15px",
              fontWeight: 700,
              color: "#1e3a8a",
            }}
          >
            Storefront setup
          </div>
          <div
            style={{
              fontSize: isMobile ? "12px" : "13px",
              color: "#1d4ed8",
              marginTop: "2px",
            }}
          >
            Status: {setupStatusLabelMap[setupStatus] || setupStatus} ({completionPercent}% complete)
          </div>
        </div>

        <button
          type="button"
          disabled={!canOpenSetup}
          onClick={handleSetupClick}
          style={{
            border: "1px solid #bfdbfe",
            background: "#eff6ff",
            color: "#1d4ed8",
            borderRadius: "8px",
            padding: isMobile ? "8px 10px" : "9px 12px",
            fontSize: isMobile ? "12px" : "13px",
            fontWeight: 600,
            cursor: canOpenSetup ? "pointer" : "not-allowed",
            opacity: canOpenSetup ? 1 : 0.85,
          }}
          title={
            canOpenSetup ? "Open storefront setup" : "Setup route unavailable"
          }
        >
          Set up storefront
        </button>
      </div>

      {!isComplete && (
        <div
          style={{
            marginTop: "10px",
            fontSize: isMobile ? "12px" : "13px",
            color: "#334155",
          }}
        >
          Missing:{" "}
          {(missingRequirementLabels.length > 0
            ? missingRequirementLabels
            : missingRequirements.map((key) => labelMap[key] || key)
          ).join(", ")}
        </div>
      )}

      {canPublish === false && (
        <div
          style={{
            marginTop: "10px",
            fontSize: isMobile ? "12px" : "13px",
            color: "#991b1b",
            fontWeight: 600,
          }}
        >
          Publishing is currently blocked until required setup is completed.
        </div>
      )}

      {canRemainVisible === false && (
        <div
          style={{
            marginTop: "10px",
            fontSize: isMobile ? "12px" : "13px",
            color: "#991b1b",
            fontWeight: 600,
          }}
        >
          Your storefront is hidden until required setup is completed.
        </div>
      )}

      {Array.isArray(notifications) && notifications.length > 0 && (
        <div style={{ marginTop: "10px", display: "grid", gap: "6px" }}>
          {notifications.slice(0, 2).map((item, index) => (
            <div
              key={`${item.title || "note"}-${index}`}
              style={{
                fontSize: isMobile ? "12px" : "13px",
                color: item.level === "error" ? "#991b1b" : "#334155",
              }}
            >
              <strong>{item.title}:</strong> {item.message}
            </div>
          ))}
        </div>
      )}

      {isComplete && (
        <div
          style={{
            marginTop: "10px",
            fontSize: isMobile ? "12px" : "13px",
            color: "#166534",
          }}
        >
          Required storefront fields are complete.
        </div>
      )}

      <div
        style={{
          marginTop: "10px",
          display: "flex",
          gap: "8px",
          flexWrap: "wrap",
        }}
      >
        {Object.entries(requiredFields).map(([key, done]) => (
          <span
            key={key}
            style={{
              fontSize: "11px",
              padding: "4px 8px",
              borderRadius: "999px",
              border: `1px solid ${done ? "#86efac" : "#fecaca"}`,
              background: done ? "#f0fdf4" : "#fef2f2",
              color: done ? "#166534" : "#991b1b",
            }}
          >
            {done ? "Done" : "Missing"}: {labelMap[key] || key}
          </span>
        ))}
      </div>
    </div>
  );
}

export default StorefrontSetupStatusBanner;
